
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Home() {
  const [activeCollection, setActiveCollection] = useState('marble');
  const [darkMode, setDarkMode] = useState(false); // Changed back to light mode default
  const [showMenu, setShowMenu] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const [subtitleVisible, setSubtitleVisible] = useState(false);
  const [buttonsVisible, setButtonsVisible] = useState(false);
  const [logoScrollPosition, setLogoScrollPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const { t, i18n } = useTranslation();

  // ----------- Effects ----------
  useEffect(() => {
    // Hero title fade in
    const timer1 = setTimeout(() => setHeroVisible(true), 500);
    const timer2 = setTimeout(() => setSubtitleVisible(true), 1500);
    const timer3 = setTimeout(() => setButtonsVisible(true), 2500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  useEffect(() => {
    // Scroll animation observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.scroll-animate');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // ----------- Helpers ----------
  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ar' : 'en';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
  };

  // Logo tape control handlers
  const handleLogoWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const scrollSpeed = 2;
    setLogoScrollPosition(prev => prev + (e.deltaX || e.deltaY) * scrollSpeed);
  };

  const handleLogoMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart(e.clientX);
    e.preventDefault();
  };

  const handleLogoMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const dragDistance = e.clientX - dragStart;
    setLogoScrollPosition(prev => prev - dragDistance);
    setDragStart(e.clientX);
  };

  const handleLogoMouseUp = () => {
    setIsDragging(false);
  };

  const handleLogoMouseLeave = () => {
    setIsDragging(false);
  };

  const collections = {
    marble: {
      title: 'Premium Marble',
      description: 'Exquisite marble varieties from around the world',
      products: [
        { name: 'Carrara White', origin: 'Italy', finish: 'Polished' },
        { name: 'Emperador Dark', origin: 'Spain', finish: 'Honed' },
        { name: 'Perlatino', origin: 'Italy', finish: 'Polished' },
        { name: 'Crema Marfil', origin: 'Spain', finish: 'Polished' },
        { name: 'Cecilia', origin: 'Italy', finish: 'Polished' },
        { name: 'Black Marquina', origin: 'Spain', finish: 'Polished' },
        { name: 'Dark Olive', origin: 'Turkey', finish: 'Polished' },
        { name: 'Tundra Gray', origin: 'Turkey', finish: 'Polished' },
        { name: 'Mugla', origin: 'Turkey', finish: 'Polished' },
        { name: 'Travertine', origin: 'Turkey', finish: 'Various' },
        { name: 'Desert', origin: 'Oman', finish: 'Polished' },
        { name: 'Uno Crema', origin: 'Turkey', finish: 'Polished' },
        { name: 'Black Absolute', origin: 'India', finish: 'Polished' },
        { name: 'Granite', origin: 'Saudi Arabia', finish: 'Polished' },
        { name: 'Royal Gray', origin: 'Oman', finish: 'Polished' },
        { name: 'Pietra Gray', origin: 'UAE', finish: 'Polished' },
        { name: 'Galaxy', origin: 'India', finish: 'Polished' }
      ]
    },
    saudi_ceramic: {
      title: 'Saudi Ceramic',
      description: 'Locally manufactured ceramic tiles supporting Saudi Vision 2030',
      products: [
        { name: 'Forma Ivory', size: '60x60x10mm', finish: 'Matt' },
        { name: 'Queen White', size: '60x60x10mm', finish: 'Glossy' },
        { name: 'Huelva White', size: '60x120x10mm', finish: 'Natural' },
        { name: 'Wonder Ivory', size: '60x120x10mm', finish: 'Matt' }
      ]
    },
    saudi_porcelain: {
      title: 'Saudi Porcelain',
      description: 'High-quality porcelain tiles manufactured locally in Saudi Arabia',
      products: [
        { name: 'SK612442', size: '60x120x10mm', finish: 'Natural' },
        { name: '5032 (Gray-End)', size: '60x60x10mm', finish: 'Polished' },
        { name: 'Arabescato', size: '60x120x10mm', finish: 'Matt' },
        { name: '61222PRS', size: '60x60x10mm', finish: 'Textured' }
      ]
    },
    explore_more: {
      title: 'Explore More',
      description:
        'Discover our complete range of premium building materials and comprehensive services',
      products: []
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openWhatsApp = (message: string) => {
    try {
      const whatsappNumber = '+966594866819';
      const encoded = encodeURIComponent(message);
      window.open(`https://wa.me/${whatsappNumber}?text=${encoded}`, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to open WhatsApp:', error);
    }
  };

  const handleGetQuote = () => {
    openWhatsApp('Hello, I would like to get a quote for your building materials.');
  };

  const handleContactForm = () => {
    openWhatsApp('Hello, I would like to contact you regarding your services.');
  };

  const getProductImageQuery = (productName: string, collection: string, index: number) => {
    // Pre‑defined URLs for known products
    const staticImages: Record<string, string> = {
      'Carrara White':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/c52fee0e8675abc2c8dfc3390cb55a61.png',
      'Emperador Dark':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/4cc11d0d79039b937c84087c0c4e1809.png',
      'Perlatino':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/a4eb7b7619ca15a97cbaaf476d87c43e.png',
      'Crema Marfil':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/2ada4aae75ba56dc7936a44996bc88f4.png',
      'Cecilia':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/a922d3e06340e69b1e487d906f6188e3.png',
      'Black Marquina':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/00e1f4ad740d8de716802b7010494fdb.png',
      'Dark Olive':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/cc6ff427486f66c2419c7b3d4ebc7c4a.png',
      'Tundra Gray':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/47326c62323415f0b0a011748731dad7.png',
      'Mugla':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/5a09b493032be3390c16247c5cc5f670.png',
      'Travertine':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/e857e13bd4f128cee4b16ea9414832bd.png',
      'Desert':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/192d25c1057d4c880d3481dac4e5a1fd.png',
      'Uno Crema':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/fd49bbb71045afc2670decf35cd9a6da.png',
      'Black Absolute':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/40f9b0c2f796bc0cd8b2799a9ff71d13.png',
      'Granite':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/b660566610548af0c85f46372c0c597d.png',
      'Royal Gray':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/7704214bd6f5e1b4193b003b139bf4af.png',
      'Pietra Gray':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/bdb25d75131029790229f79f8626dadf.png',
      'Galaxy':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/e5679b86e1d244ab8b7ab5924e57c453.png',

      'Forma Ivory':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/3c279d4fec54bc5a526836c61c25b5e1.png',
      'Queen White':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/e5e83905ede62ecf704c66a06914e731.png',
      'Huelva White':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/ca4e5a0657d79a8a26a3327ec2707b46.png',
      'Wonder Ivory':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/eb51787ced245db39919f1cddcd81557.png',
      SK612442:
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/ab950f963dded782214153a32b3f7808.png',
      '5032 (Gray-End)':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/b13cec1033be93e25f398bd66bac5456.png',
      Arabescato:
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/bd5fb4bbc61c4decf35b589eaa42a771.png',
      '61222PRS':
        'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/bef56703f0366733218660083506a002.png'
    };

    if (staticImages[productName]) {
      return staticImages[productName];
    }

    // Fallback – generate a query‑based image URL
    const queries: Record<string, string> = {
      'City Cream':
        'Cream colored ceramic tile, urban modern design, Saudi tile with matte finish, contemporary ceramic texture',
      'Desert Sand':
        'Sandy beige porcelain tile, desert‑inspired color, Saudi manufactured porcelain, natural sand texture ceramic surface',
      'Pearl White':
        'Pearl white porcelain with subtle shimmer, polished ceramic surface, Saudi porcelain tile, elegant white stone‑look tile',
      'Stone Grey':
        'Medium gray porcelain tile, stone texture finish, Saudi manufactured ceramic, modern gray tile with natural pattern',
      'Marble Beige':
        'Beige porcelain with marble veining, textured ceramic surface, Saudi tile with natural stone pattern, warm neutral color',
      'Granite Countertops':
        'Premium granite countertop surface, polished black granite with natural patterns, kitchen counter material, luxury stone surface',
      'Mosaic Tiles':
        'Colorful mosaic tile pattern, small decorative tiles arranged in artistic design, bathroom wall tiles, premium mosaic surface',
      'Natural Stone Cladding':
        'Natural stone wall cladding, textured exterior stone panels, architectural stone facade, building exterior material',
      'Luxury Vinyl Tiles':
        'High‑end vinyl flooring tiles, wood‑look luxury vinyl planks, modern flooring material, embossed surface texture'
    };

    const query =
      queries[productName] ??
      `${productName} building material texture close‑up professional photography clean background high quality surface detail`;

    const encodedQuery = encodeURIComponent(query);

    return `https://readdy.ai/api/search-image?query=$%7BencodedQuery%7D&width=300&height=300&seq=${activeCollection}-${productName
      .replace(/\s+/g, '-')
      .toLowerCase()}-${index}-${Date.now()}&orientation=squarish`;
  };

  // ----------- Render ----------
  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Header */}
      <header
        className={`shadow-sm transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 md:py-6">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src="https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/b17c34dcec7386fbb12e900875d513e6.jpeg"
                alt="Prime Materials Logo"
                className="h-8 md:h-12 w-auto transition-transform duration-300 hover:scale-110 cursor-pointer"
              />
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setShowMenu(!showMenu)}
                className={`p-2 rounded-lg transition-colors cursor-pointer ${
                  darkMode ? 'text-gray-300 hover-bg-gray-700' : 'text-gray-700 hover-bg-gray-100'
                }`}
              >
                <i className={`text-xl ${showMenu ? 'ri-close-line' : 'ri-menu-line'}`} />
              </button>
            </div>

            {/* Desktop controls */}
            <div className="hidden md:flex items-center space-x-6">
              {/* Language toggle */}
              <button
                onClick={toggleLanguage}
                className={`px-3 py-1 rounded-lg font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  darkMode
                    ? 'text-gray-300 hover-text-amber-400 hover-bg-gray-700'
                    : 'text-gray-700 hover-text-amber-700 hover-bg-gray-100'
                }`}
              >
                {i18n.language === 'en' ? 'العربية' : 'English'}
              </button>

              {/* Dark mode switch */}
              <div
                className={`flex items-center rounded-full p-1 transition-colors duration-300 ${
                  darkMode ? 'bg-gray-700' : 'bg-gray-100'
                }`}
              >
                <button
                  onClick={() => setDarkMode(false)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                    !darkMode ? 'bg-amber-700 text-white' : 'text-gray-600 hover-text-amber-700'
                  }`}
                >
                  <i className="ri-sun-line text-sm" />
                </button>
                <button
                  onClick={() => setDarkMode(true)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                    darkMode ? 'bg-amber-700 text-white' : 'text-gray-600 hover-text-amber-700'
                  }`}
                >
                  <i className="ri-moon-line text-sm" />
                </button>
              </div>

              {/* Navigation (desktop) */}
              <nav className="flex space-x-12">
                <a
                  href="#collections"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('collections');
                  }}
                  className={`font-medium cursor-pointer transition-colors duration-300 ${
                    darkMode ? 'text-gray-300 hover-text-amber-400' : 'text-gray-700 hover-text-amber-700'
                  }`}
                >
                  {t('collections')}
                </a>
                <a
                  href="#about"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('about');
                  }}
                  className={`font-medium cursor-pointer transition-colors duration-300 ${
                    darkMode ? 'text-gray-300 hover-text-amber-400' : 'text-gray-700 hover-text-amber-700'
                  }`}
                >
                  {t('about')}
                </a>
                <a
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection('contact');
                  }}
                  className={`font-medium cursor-pointer transition-colors duration-0 ${
                    darkMode ? 'text-gray-300 hover-text-amber-400' : 'text-gray-700 hover-text-amber-700'
                  }`}
                >
                  {t('contact')}
                </a>
              </nav>

              {/* Get Quote button */}
              <button
                onClick={handleGetQuote}
                className="bg-amber-700 text-white px-6 py-2 rounded-lg hover-bg-amber-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('getQuote')}
              </button>
            </div>
          </div>

          {/* Mobile menu dropdown */}
          {showMenu && (
            <div className={`md:hidden border-t transition-colors duration-300 ${
              darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
            }`}>
              <div className="py-4 space-y-4">
                {/* Mobile navigation */}
                <nav className="space-y-2">
                  <a
                    href="#collections"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('collections');
                      setShowMenu(false);
                    }}
                    className={`block px-4 py-2 font-medium cursor-pointer transition-colors duration-300 ${
                      darkMode ? 'text-gray-300 hover-text-amber-400' : 'text-gray-700 hover-text-amber-700'
                    }`}
                  >
                    {t('collections')}
                  </a>
                  <a
                    href="#about"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('about');
                      setShowMenu(false);
                    }}
                    className={`block px-4 py-2 font-medium cursor-pointer transition-colors duration-300 ${
                      darkMode ? 'text-gray-300 hover-text-amber-400' : 'text-gray-700 hover-text-amber-700'
                    }`}
                  >
                    {t('about')}
                  </a>
                  <a
                    href="#contact"
                    onClick={(e) => {
                      e.preventDefault();
                      scrollToSection('contact');
                      setShowMenu(false);
                    }}
                    className={`block px-4 py-2 font-medium cursor-pointer transition-colors duration-300 ${
                      darkMode ? 'text-gray-300 hover-text-amber-400' : 'text-gray-700 hover-text-amber-700'
                    }`}
                  >
                    {t('contact')}
                  </a>
                </nav>

                {/* Mobile controls */}
                <div className="px-4 space-y-4">
                  {/* Language toggle */}
                  <button
                    onClick={toggleLanguage}
                    className={`w-full text-left px-3 py-2 rounded-lg font-medium transition-colors cursor-pointer ${
                      darkMode
                        ? 'text-gray-300 hover-text-amber-400 hover-bg-gray-700'
                        : 'text-gray-700 hover-text-amber-700 hover-bg-gray-100'
                    }`}
                  >
                    {i18n.language === 'en' ? 'العربية' : 'English'}
                  </button>

                  {/* Dark mode switch */}
                  <div className="flex items-center justify-between">
                    <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {darkMode ? 'Dark Mode' : 'Light Mode'}
                    </span>
                    <div
                      className={`flex items-center rounded-full p-1 transition-colors duration-300 ${
                        darkMode ? 'bg-gray-700' : 'bg-gray-100'
                      }`}
                    >
                      <button
                        onClick={() => setDarkMode(false)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                          !darkMode ? 'bg-amber-700 text-white' : 'text-gray-600 hover-text-amber-700'
                        }`}
                      >
                        <i className="ri-sun-line text-sm" />
                      </button>
                      <button
                        onClick={() => setDarkMode(true)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer ${
                          darkMode ? 'bg-amber-700 text-white' : 'text-gray-600 hover-text-amber-700'
                        }`}
                      >
                        <i className="ri-moon-line text-sm" />
                      </button>
                    </div>
                  </div>

                  {/* Get Quote button */}
                  <button
                    onClick={() => {
                      handleGetQuote();
                      setShowMenu(false);
                    }}
                    className="w-full bg-amber-700 text-white px-6 py-3 rounded-lg hover-bg-amber-800 transition-colors cursor-pointer"
                  >
                    {t('getQuote')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Dynamic Background based on theme */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1000"
          style={{
            backgroundImage: darkMode
              ? `url('https://readdy.ai/api/search-image?query=Dramatic%20mountain%20peak%20summit%20with%20dark%20granite%20stone%20formations%2C%20moody%20atmospheric%20lighting%2C%20luxury%20natural%20stone%20quarry%20on%20mountain%20top%2C%20dark%20stormy%20sky%2C%20professional%20architectural%20photography%2C%20premium%20natural%20stone%20environment%2C%20majestic%20mountain%20landscape%20with%20exposed%20rock%20formations&width=1920&height=1080&seq=mountain-stone-quarry-dark&orientation=landscape')`
              : `url('https://readdy.ai/api/search-image?query=Same%20dramatic%20mountain%20peak%20summit%20with%20granite%20stone%20formations%20but%20in%20bright%20sunny%20afternoon%2C%20golden%20sunlight%20illuminating%20the%20mountain%20peaks%2C%20clear%20blue%20sky%20with%20white%20clouds%2C%20luxury%20natural%20stone%20quarry%20on%20mountain%20top%20in%20daylight%2C%20professional%20architectural%20photography%2C%20premium%20natural%20stone%20environment%2C%20majestic%20mountain%20landscape%20with%20exposed%20rock%20formations%20in%20bright%20sunlight&width=1920&height=1080&seq=mountain-stone-quarry-bright&orientation=landscape')`
          }}
        >
          <div className={`absolute inset-0 transition-all duration-1000 ${
            darkMode
              ? 'bg-gradient-to-r from-black/70 to-black/40'
              : 'bg-gradient-to-r from-black/30 to-black/10'
          }`} />
        </div>

        {/* Dark Mode: Animated Rain Effect */}
        {darkMode && (
          <div className="absolute inset-0 pointer-events-none z-10">
            {[...Array(100)].map((_, i) => (
              <div
                key={`rain-${i}`}
                className="absolute bg-gradient-to-b from-gray-600/80 to-gray-800/60 animate-rain"
                style={{
                  left: `${Math.random() * 100}%`,
                  width: `${Math.random() * 2 + 1}px`,
                  height: `${Math.random() * 100 + 50}px`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${Math.random() * 0.5 + 0.5}s`,
                  transform: `rotate(${Math.random() * 10 + 15}deg)`,
                  borderRadius: '50px',
                  top: '-150px'
                }}
              />
            ))}
          </div>
        )}

        {/* Dark Mode: Thunder Effects - Fixed timing */}
        {darkMode && (
          <div className="absolute inset-0 pointer-events-none z-20">
            {[...Array(3)].map((_, i) => (
              <div
                key={`thunder-${i}`}
                className="absolute inset-0 bg-white/2 animate-thunder-controlled opacity-0"
                style={{
                  animationDelay: `${i * 5}s`,
                  animationDuration: '0.15s'
                }}
              />
            ))}
          </div>
        )}

        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
          <h1
            className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-4 md:mb-6 tracking-tight leading-tight transition-all duration-1000 ${
              heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
            style={{ fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif', fontWeight: '700' }}
          >
            {i18n.language === 'ar' ? (
              <>
                <span className="block sm:hidden">
                  Built on stone
                  <br />
                  defined by <span className="text-amber-600">quality</span>
                </span>
                <span className="hidden sm:block">
                  Built on stone,
                  <br className="block sm:hidden" />
                  <span className="block sm:inline"> defined by</span>
                  <span className="text-amber-600"> quality</span>
                </span>
              </>
            ) : (
              <>
                <span className="block sm:hidden">
                  Built on stone, 
                  <br />
                  defined by <span className="text-amber-600">quality</span>
                </span>
                <span className="hidden sm:block">
                  Built on stone,
                  <br className="hidden sm:block" />
                  defined by <span className="text-amber-600">quality</span>
                </span>
              </>
            )}
          </h1>
          <p
            className={`text-base sm:text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 opacity-90 font-light transition-all duration-1000 delay-500 px-2 ${
              subtitleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {t('heroSubtitle')}
          </p>
          <div
            className={`transition-all duration-1000 delay-1000 ${
              buttonsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {/* Arabic mobile: vertical stack, English: horizontal */}
            <div className={`flex gap-3 sm:gap-4 justify-center items-center ${
              i18n.language === 'ar' ? 'flex-col' : 'flex-col sm:flex-row'
            }`}>
              <button
                onClick={() => scrollToSection('collections')}
                className="w-full sm:w-auto bg-amber-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover-bg-amber-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('exploreCollections')}
              </button>
              <button
                onClick={handleContactForm}
                className="w-full sm:w-auto border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover-bg-white hover-text-gray-900 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('contactWhatsApp')}
              </button>
              <button
                onClick={() => window.open('https://linktr.ee/prime.materials', '_blank')}
                className="w-full sm:w-auto bg-amber-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg text-base sm:text-lg font-semibold hover-bg-amber-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                {t('checkLinkTree')}
              </button>
            </div>
          </div>
        </div>

      </section>

      {/* Collections Section */}
      <section
        id="collections"
        className={`py-12 md:py-20 transition-colors duration-300 scroll-animate relative overflow-hidden ${
          darkMode ? 'bg-gray-800' : 'bg-gray-50'
        }`}
      >
        {/* Background Video */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover opacity-20"
            style={{ filter: 'brightness(0.7) contrast(1.1)' }}
          >
            <source
              src="https://readdy.ai/api/search-image?query=Slow%20motion%20video%20of%20premium%20marble%20and%20natural%20stone%20textures%2C%20elegant%20granite%20surfaces%20with%20beautiful%20veining%20patterns%2C%20luxury%20building%20materials%20showcase%2C%20smooth%20camera%20movement%20over%20polished%20stone%20surfaces%2C%20high-end%20natural%20stone%20collection%20display%2C%20cinematic%20lighting%20on%20marble%20textures&width=1920&height=1080&seq=marble-stone-video&orientation=landscape"
              type="video/mp4"
            />
          </video>
          <div className={`absolute inset-0 ${darkMode ? 'bg-gray-800/80' : 'bg-gray-50/80'}`} />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Header */}
          <div className="text-center mb-12 md:mb-16">
            <h3
              className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 transition-colors duration-300 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              {t('premiumCollections')}
            </h3>
            <p
              className={`text-lg sm:text-xl max-w-3xl mx-auto transition-colors duration-300 ${
                darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              {t('collectionsDescription')}
            </p>
          </div>

          {/* Collection tabs */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8 md:mb-12">
            {Object.entries(collections).map(([key, collection]) => (
              <button
                key={key}
                onClick={() => setActiveCollection(key)}
                className={`px-3 sm:px-6 py-2 sm:py-3 rounded-full text-sm sm:text-base font-semibold transition-colors whitespace-nowrap cursor-pointer backdrop-blur-sm ${
                  activeCollection === key
                    ? 'bg-amber-700 text-white shadow-lg'
                    : `${darkMode ? 'bg-gray-700/80 text-gray-300 hover-bg-gray-600/80 hover-text-amber-400' : 'bg-white/80 text-gray-700 hover-bg-amber-50/80 hover-text-amber-700'} shadow-md`
                }`}
              >
                {t(`collection.${key}.title`)}
              </button>
            ))}
          </div>

          {/* Active collection content */}
          <div
            className={`rounded-2xl shadow-lg p-4 sm:p-6 md:p-8 transition-colors duration-300 backdrop-blur-sm ${
              darkMode ? 'bg-gray-700/90' : 'bg-white/90'
            }`}
            data-product-shop
          >
            <div className="text-center mb-6 md:mb-8">
              <h4
                className={`text-xl sm:text-2xl md:text-3xl font-bold mb-4 transition-colors duration-300 ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                {t(`collection.${activeCollection}.title`)}
              </h4>
              <p
                className={`text-base sm:text-lg transition-colors duration-300 ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}
              >
                {t(`collection.${activeCollection}.description`)}
              </p>
            </div>

            {activeCollection === 'explore_more' ? (
              <div className="text-center py-8 md:py-16">
                <div className="max-w-2xl mx-auto">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-amber-700 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <i className="ri-customer-service-2-line text-white text-2xl md:text-3xl" />
                  </div>
                  <h5
                    className={`text-xl md:text-2xl font-bold mb-4 transition-colors duration-300 ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {t('professionalConsultation')}
                  </h5>
                  <p
                    className={`text-base md:text-lg mb-6 md:mb-8 leading-relaxed transition-colors duration-300 ${
                      darkMode ? 'text-gray-300' : 'text-gray-600'
                    }`}
                  >
                    {t('consultationDescription')}
                  </p>
                  <div className="flex justify-center">
                    <button
                      onClick={handleContactForm}
                      className="bg-amber-700 text-white px-6 md:px-8 py-3 md:py-4 rounded-lg text-base md:text-lg font-semibold hover-bg-amber-800 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center shadow-lg"
                    >
                      <i className="ri-phone-line mr-2" />
                      {t('requestConsultation')}
                    </button>
                  </div>
                  <div
                    className={`mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 text-sm transition-colors duration-300 ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    <div className="flex items-center justify-center">
                      <i className="ri-file-text-line mr-2" />
                      {t('productSpecifications')}
                    </div>
                    <div className="flex items-center justify-center">
                      <i className="ri-price-tag-3-line mr-2" />
                      {t('pricingInformation')}
                    </div>
                    <div className="flex items-center justify-center">
                      <i className="ri-customer-service-2-line mr-2" />
                      {t('professionalServices')}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {collections[activeCollection as keyof typeof collections].products.map(
                  (product, index) => (
                    <div
                      key={index}
                      className={`rounded-lg p-4 md:p-6 hover-shadow-md transition-all duration-300 backdrop-blur-sm ${
                        darkMode ? 'bg-gray-600/80' : 'bg-gray-50/80'
                      }`}
                    >
                      <div
                        className="w-full h-32 sm:h-40 md:h-48 bg-cover bg-center rounded-lg mb-4 shadow-md"
                        style={{
                          backgroundImage: `url('${getProductImageQuery(
                            product.name,
                            activeCollection,
                            index
                          )}')`
                        }}
                      />
                      <h5
                        className={`font-semibold mb-2 text-sm md:text-base transition-colors duration-3 ${
                          darkMode ? 'text-white' : 'text-gray-900'
                        }`}
                      >
                        {product.name}
                      </h5>
                      <div
                        className={`text-xs md:text-sm space-y-1 transition-colors duration-3 ${
                          darkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}
                      >
                        {'size' in product && <p>{t('size')}: {product.size}</p>}
                        {'thickness' in product && <p>{t('thickness')}: {product.thickness}</p>}
                        {'origin' in product && <p>{t('origin')}: {product.origin}</p>}
                        {'category' in product && <p>{t('category')}: {product.category}</p>}
                        <p>{t('finish')}: {product.finish}</p>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section
        id="about"
        className={`py-12 md:py-20 transition-colors duration-300 scroll-animate ${
          darkMode ? 'bg-gray-900' : 'bg-white'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h3
                className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 transition-colors duration-300 ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                {t('aboutTitle')}
              </h3>
              <p
                className={`text-base md:text-lg mb-4 transition-colors duration-300 ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}
              >
                {t('aboutDescription')}
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-amber-700 text-xl" />
                  </div>
                  <div className="ml-3">
                    <h4
                      className={`font-semibold text-sm md:text-base transition-colors duration-30 ${
                        darkMode ? 'text-white' : 'text-gray-900'
                      }`}
                    >
                      {t('premiumQuality')}
                    </h4>
                    <p
                      className={`text-sm md:text-base transition-colors duration-30 ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}
                    >
                      {t('premiumQualityDesc')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-amber-700 text-xl" />
                  </div>
                  <div className="ml-3">
                    <h4
                      className={`font-semibold text-sm md:text-base transition-colors duration-30 ${
                        darkMode ? 'text-white' : 'text-gray-900'
                      }`}
                    >
                      {t('localManufacturing')}
                    </h4>
                    <p
                      className={`text-sm md:text-base transition-colors duration-30 ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}
                    >
                      {t('localManufacturingDesc')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-amber-700 text-xl" />
                  </div>
                  <div className="ml-3">
                    <h4
                      className={`font-semibold text-sm md:text-base transition-colors duration-30 ${
                        darkMode ? 'text-white' : 'text-gray-900'
                      }`}
                    >
                      {t('expertService')}
                    </h4>
                    <p
                      className={`text-sm md:text-base transition-colors duration-30 ${
                        darkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}
                    >
                      {t('expertServiceDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="h-64 md:h-96 bg-cover bg-center rounded-2xl"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Professional%20building%20materials%20warehouse%20showroom%20in%20Saudi%20Arabia%20with%20organized%20displays%20of%20tiles%20marble%20granite%2C%20modern%20industrial%20interior%20%2C%20professional%20lighting%20clean%20organized%20space&width=600&height=400&seq=about-image&orientation=landscape')`
              }}
            />
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className={`py-12 md:py-20 transition-colors duration-300 scroll-animate ${
          darkMode ? 'bg-gray-800' : 'bg-gray-50'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12 md:mb-16">
            <h3
              className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 transition-colors duration-300 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}
            >
              {i18n.language === 'ar' ? 'لنبني معاً!' : "Let's build together!"}
            </h3>
          </div>

          {/* Contact Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Phone */}
            <div className="text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-amber-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="ri-phone-line text-white text-xl md:text-2xl" />
              </div>
              <h4
                className={`font-semibold mb-2 text-base md:text-lg transition-colors duration-300 ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                {i18n.language === 'ar' ? 'الهاتف' : 'Phone'}
              </h4>
              <div className="space-y-1">
                <a
                  href="tel:+966594866819"
                  className={`block text-sm md:text-base transition-colors duration-300 cursor-pointer ${
                    darkMode ? 'text-gray-300 hover-text-amber-400' : 'text-gray-600 hover-text-amber-700'
                  }`}
                  dir={i18n.language === 'ar' ? 'ltr' : 'ltr'}
                >
                  +966 594 8668 19
                </a>
                <a
                  href="tel:+966555545400"
                  className={`block text-sm md:text-base transition-colors duration-300 cursor-pointer ${
                    darkMode ? 'text-gray-300 hover-text-amber-400' : 'text-gray-600 hover-text-amber-700'
                  }`}
                  dir={i18n.language === 'ar' ? 'ltr' : 'ltr'}
                >
                  +966 5555 454 00
                </a>
              </div>
            </div>

            {/* Email */}
            <div className="text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-amber-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="ri-mail-line text-white text-xl md:text-2xl" />
              </div>
              <h4
                className={`font-semibold mb-2 text-base md:text-lg transition-colors duration-300 ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                {i18n.language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
              </h4>
              <div className="space-y-1">
                <a
                  href="mailto:info@pri-m.com"
                  className={`block text-sm md:text-base transition-colors duration-300 cursor-pointer ${
                    darkMode ? 'text-gray-300 hover-text-amber-400' : 'text-gray-600 hover-text-amber-700'
                  }`}
                >
                  info@pri-m.com
                </a>
                <a
                  href="mailto:info@amoudiest.com"
                  className={`block text-sm md:text-base transition-colors duration-300 cursor-pointer ${
                    darkMode ? 'text-gray-300 hover-text-amber-400' : 'text-gray-600 hover-text-amber-700'
                  }`}
                >
                  info@amoudiest.com
                </a>
              </div>
            </div>

            {/* Location */}
            <div className="text-center">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-amber-700 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <i className="ri-map-pin-line text-white text-xl md:text-2xl" />
              </div>
              <h4
                className={`font-semibold mb-2 text-base md:text-lg transition-colors duration-300 ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}
                >
                {i18n.language === 'ar' ? 'الموقع' : 'Location'}
                </h4>
              <div className="space-y-1">
                <p
                  className={`text-sm md:text-base transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}
                >
                  {i18n.language === 'ar' ? 'جدة، الشاطئ' : 'Jeddah, Ash Shati'}
                </p>
                <p
                  className={`text-sm md:text-base transition-colors duration-300 ${
                    darkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}
                >
                  {i18n.language === 'ar' ? 'بحرة، المرسلات' : 'Bahrah, Almarsalat'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted Clients Section - Now the last section */}
      <section className="py-12 md:py-16 bg-gray-900 overflow-hidden scroll-animate">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-12">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">{t('trustedClients')}</h3>
            {t('trustedClientsDesc') && (
              <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto">{t('trustedClientsDesc')}</p>
            )}
          </div>

          {/* New elegant logo carousel */}
          <div className="relative">
            {/* Main carousel container */}
            <div className="overflow-hidden">
              <div className="flex animate-logo-carousel">
                {/* Triple the logos for seamless infinite loop */}
                {Array(3).fill([
                  'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/f6e24c0706c634a75dbae8140e8ad7ee.png',
                  'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/f2bf79484df69183664a675bf39563fd.jpeg',
                  'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/7bf46acd11708b37fa458c08edcd5eae.png',
                  'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/73cf84ac7ed0c7a342240ded2f7ce27a.jpeg',
                  'https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/4d2c77c031a34bf7cf720cd457166640.png'
                ]).flat().map((src, idx) => (
                  <div
                    key={`logo-${idx}`}
                    className="flex-shrink-0 mx-3 md:mx-6"
                    style={{ 
                      width: '200px',
                      animationDelay: `${(idx % 5) * 0.2}s`
                    }}
                  >
                    <div className="bg-white rounded-xl p-3 md:p-6 h-16 md:h-24 flex items-center justify-center shadow-lg border border-gray-200 hover:shadow-xl transition-shadow duration-300 animate-logo-fade-in">
                      <img
                        src={src}
                        alt={idx % 5 === 4 ? 'Ministry of Commerce Saudi Arabia' : `Trusted Client ${(idx % 5) + 1}`}
                        className="max-h-10 md:max-h-16 max-w-full object-contain"
                        loading="lazy"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Gradient overlays for smooth fade effect */}
            <div className="absolute top-0 left-0 w-16 md:w-32 h-full bg-gradient-to-r from-gray-900 to-transparent pointer-events-none z-10"></div>
            <div className="absolute top-0 right-0 w-16 md:w-32 h-full bg-gradient-to-l from-gray-900 to-transparent pointer-events-none z-10"></div>
          </div>

          {/* Footer copyright - conditional based on language */}
          <div className="text-center mt-12 md:mt-16 pt-6 md:pt-8 border-t border-gray-800">
            {i18n.language === 'ar' ? (
              // Arabic version - simplified footer
              <div className="flex flex-col md:flex-row justify-center items-center gap-4">
                <p className="text-gray-400 text-sm">© 2025 Prime Materials. جميع الحقوق محفوظة.</p>
                <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4 text-gray-400 text-sm">
                  <div className="flex items-center gap-2" dir="ltr">
                    <i className="ri-phone-line text-amber-700" />
                    <span>+966 594 8668 19</span>
                  </div>
                  <div className="flex items-center gap-2" dir="ltr">
                    <i className="ri-phone-line text-amber-700" />
                    <span>+966 5555 454 00</span>
                  </div>
                </div>
                <a
                  href="https://readdy.ai/?origin=logo"
                  target="_blank"
                  rel="noopener"
                  className="text-gray-400 hover-text-amber-400 transition-colors text-sm cursor-pointer"
                >
                  Website Builder
                </a>
              </div>
            ) : (
              // English version - full footer
              <div className="flex flex-col md:flex-row justify-center items-center gap-4">
                <p className="text-gray-400 text-sm">© 2025 Prime Materials. All rights reserved.</p>
                <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4 text-gray-400 text-sm">
                  <div className="flex items-center gap-2" dir="ltr">
                    <i className="ri-phone-line text-amber-700" />
                    <span>+966 594 8668 19</span>
                  </div>
                  <div className="flex items-center gap-2" dir="ltr">
                    <i className="ri-phone-line text-amber-700" />
                    <span>+966 5555 454 00</span>
                  </div>
                </div>
                <a
                  href="https://readdy.ai/?origin=logo"
                  target="_blank"
                  rel="noopener"
                  className="text-gray-400 hover-text-amber-400 transition-colors text-sm cursor-pointer"
                >
                  Website Builder
                </a>
              </div>
            )}
          </div>

        </div>
      </section>

      {/* Footer - only show for English version */}
      {i18n.language !== 'ar' && (
        <footer className="py-16 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Company Info */}
              <div>
                <div className="flex items-center mb-4">
                  <img
                    src="https://static.readdy.ai/image/c4f9938401d972f6ee79c2ac6b744bf9/b17c34dcec7386fbb12e900875d513e6.jpeg"
                    alt="Prime Materials Logo"
                    className="h-16 w-auto"
                  />
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed">
                  Built on stone, defined by quality
                </p>
                <div className="flex space-x-3">
                  <a
                    href="https://wa.me/966594866819"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-amber-700 rounded-lg flex items-center justify-center text-white hover-bg-amber-800 transition-colors cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-lg" />
                  </a>
                  <a
                    href="mailto:info@pri-m.com"
                    className="w-10 h-10 bg-amber-700 rounded-lg flex items-center justify-center text-white hover-bg-amber-800 transition-colors cursor-pointer"
                  >
                    <i className="ri-mail-line text-lg" />
                  </a>
                  <a
                    href="tel:+966594866819"
                    className="w-10 h-10 bg-amber-700 rounded-lg flex items-center justify-center text-white hover-bg-amber-800 transition-colors cursor-pointer"
                  >
                    <i className="ri-phone-line text-lg" />
                  </a>
                </div>
              </div>

              {/* Collections */}
              <div>
                <h4 className="text-white font-semibold mb-6 text-lg">Collections</h4>
                <ul className="space-y-3">
                  <li>
                    <span className="text-gray-300 hover-text-amber-400 transition-colors cursor-pointer">
                      Premium Marble
                    </span>
                  </li>
                  <li>
                    <span className="text-gray-300 hover-text-amber-400 transition-colors cursor-pointer">
                      Natural Stones
                    </span>
                  </li>
                  <li>
                    <span className="text-gray-300 hover-text-amber-400 transition-colors cursor-pointer">
                      High-End Porcelain
                    </span>
                  </li>
                  <li>
                    <span className="text-gray-300 hover-text-amber-400 transition-colors cursor-pointer">
                      Saudi Ceramic
                    </span>
                  </li>
                  <li>
                    <span className="text-gray-300 hover-text-amber-400 transition-colors cursor-pointer">
                      Saudi Porcelain
                    </span>
                  </li>
                </ul>
              </div>

              {/* Services */}
              <div>
                <h4 className="text-white font-semibold mb-6 text-lg">Services</h4>
                <ul className="space-y-3">
                  <li>
                    <span className="text-gray-300 hover-text-amber-400 transition-colors cursor-pointer">
                      Material Consultation
                    </span>
                  </li>
                  <li>
                    <span className="text-gray-300 hover-text-amber-400 transition-colors cursor-pointer">
                      Project Planning
                    </span>
                  </li>
                  <li>
                    <span className="text-gray-300 hover-text-amber-400 transition-colors cursor-pointer">
                      Installation
                    </span>
                  </li>
                  <li>
                    <span className="text-gray-300 hover-text-amber-400 transition-colors cursor-pointer">
                      Custom Orders
                    </span>
                  </li>
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h4 className="text-white font-semibold mb-6 text-lg">Contact Info</h4>
                <ul className="space-y-4">
                  <li className="flex items-start text-gray-300">
                    <i className="ri-phone-line mr-3 mt-1 text-amber-700" />
                    <div>
                      <div>+966 594 8668 19</div>
                      <div>+966 5555 454 00</div>
                    </div>
                  </li>
                  <li className="flex items-start text-gray-300">
                    <i className="ri-mail-line mr-3 mt-1 text-amber-700" />
                    <div>
                      <div>info@pri-m.com</div>
                      <div>info@amoudiest.com</div>
                    </div>
                  </li>
                  <li className="flex items-start text-gray-300">
                    <i className="ri-map-pin-line mr-3 mt-1 text-amber-700" />
                    <div>
                      <div>Jeddah, Ash Shati</div>
                      <div>Bahrah, Almarsalat</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">© 2025 Prime Materials. All rights reserved.</p>
              <a
                href="https://readdy.ai/?origin=logo"
                target="_blank"
                rel="noopener"
                className="text-gray-400 hover-text-amber-400 transition-colors text-sm mt-2 md:mt-0 cursor-pointer"
              >
                Website Builder
              </a>
            </div>
          </div>
        </footer>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes rain {
          0% {
            transform: translateY(-250px) rotate(15deg);
            opacity: 0;
          }
          5% {
            opacity: 1;
          }
          95% {
            opacity: 1;
          }
          100% {
            transform: translateY(calc(100vh + 50px)) rotate(15deg);
            opacity: 0;
          }
        }

        @keyframes thunder-controlled {
          0% {
            opacity: 0;
          }
          5% {
            opacity: 1;
          }
          10% {
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          20% {
            opacity: 0;
          }
          100% {
            opacity: 0;
          }
        }

        @keyframes fly-bird-realistic {
          0% {
            transform: translateX(-60px) translateY(0px) scale(1) rotate(0deg);
            opacity: 0;
          }
          5% {
            opacity: 1;
          }
          25% {
            transform: translateX(25vw) translateY(-12px) scale(1.1) rotate(-5deg);
          }
          50% {
            transform: translateX(50vw) translateY(-20px) scale(1.2) rotate(0deg);
          }
          75% {
            transform: translateX(75vw) translateY(-12px) scale(1.1) rotate(5deg);
          }
          95% {
            opacity: 1;
          }
          100% {
            transform: translateX(calc(100vw + 60px)) translateY(0px) scale(1) rotate(0deg);
            opacity: 0;
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes logo-carousel {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(calc(-240px * 5 - 32px * 5));
          }
        }

        @keyframes logo-fade-in {
          0% {
            opacity: 0;
            transform: translateX(50px) scale(0.8);
          }
          100% {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }

        .animate-rain {
          animation: rain linear infinite;
          will-change: transform, opacity;
        }

        .animate-thunder-controlled {
          animation: thunder-controlled ease-in-out infinite;
          will-change: opacity;
        }

        .animate-fly-bird-realistic {
          animation: fly-bird-realistic ease-out infinite;
          will-change: transform, opacity;
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
          will-change: transform, opacity;
        }

        .animate-logo-carousel {
          animation: logo-carousel 15s linear infinite;
          will-change: transform;
        }

        .animate-logo-fade-in {
          animation: logo-fade-in 0.8s ease-out forwards;
          will-change: transform, opacity;
        }

        /* Mobile adjustments - smaller and slower */
        @media (max-width: 768px) {
          .animate-logo-carousel {
            animation: logo-carousel 12s linear infinite;
          }

          @keyframes logo-carousel {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(calc(-200px * 5 - 24px * 5));
            }
          }
        }

        .scroll-animate {
          opacity: 0;
          transform: translateY(30px);
        }

        .scroll-animate.animate-fade-in-up {
          opacity: 1;
          transform: translateY(0);
        }

        /* 120fps optimizations */
        * {
          backface-visibility: hidden;
          perspective: 1000px;
        }

        .transition-all,
        .transition-colors,
        .transition-transform {
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>
    </div>
  );
}
